import {
	ReactElement,
	useState,
	useCallback,
	useRef,
	Dispatch,
	SetStateAction,
	useMemo,
	forwardRef,
	Ref,
	useImperativeHandle,
	RefObject
} from "react";
import {
	ActionListItem,
	AlertBar,
	DropOverActionList,
	FormattedText,
	ReorderableList,
	ReorderableListAddButtonClickEventArgs,
	SearchInputForm,
	Typography
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	GlobalNavigationItemKind,
	GlobalNavigationPermissionsModel
} from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { IFormHandler } from "client/Forms";
import { Coordinate } from "client/Misc/commonTypes";
import { SpaceKind } from "client/Api/Workspace/workspace";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	EMPTY_ARRAY,
	isNotNil
} from "client/Misc/utils";
import { AppGlobalNavigationItemInfo } from "./GlobalNavigationManagement";
import { contains, normalizeForSearch } from "client/Data/searching";
import { never } from "client_legacy/ndaw/errors";
import { GlobalNavigationListItem, getItemUniqueKey } from "./GlobalNavigationListItem";
import { useDeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { IValueInputAttachable } from "client/Forms/attachable";
import { GlobalNavigationGroupAndIndex } from "./GlobalNavigationItemMenu";
import { getClientId, setClientId } from "client/Misc/clientId";
import styles from "./Form.module.less";
import {
	GlobalNavigationFormModel,
	GlobalNavigationItemFormModel,
	GroupGlobalNavigationItemFormModel,
	isGroupItem,
	SpaceGlobalNavigationItemFormModel
} from "../misc";
import { AutoFocusState } from "client/Hooks/useAutoFocus";
import { SavedSpace } from "client/Features/Workspace/EditSpaceDialog/EditSpaceDialog";

type ActionsMenuState = {
	isVisible: false;
} | {
	isVisible: true;
	coordinate: Coordinate;
};

export type FormProps = {
	attachable: IFormHandler<GlobalNavigationFormModel>;
	appItemsInfo: Map<number, AppGlobalNavigationItemInfo>;
	permissions: GlobalNavigationPermissionsModel;
	onAddAppAsTab(targetGroupIndex: number | null): void;
	setRecentlyRemovedAppIds: Dispatch<SetStateAction<readonly number[]>>;
	setAppItemsInfo: Dispatch<SetStateAction<Map<number, AppGlobalNavigationItemInfo>>>;
	onCreateSharedSpace: () => Promise<TryResult<SavedSpace>>;
	onEditSpace: (spaceId: number) => void;
	appIdToScroll: Guid | null;
	onAppIdToScrollChange: (id: Guid | null) => void;
	onChangeIsBusy: (isBusy: boolean) => void;
};

export type FormHandle = {
	createGroup(): void;
};

export const Form = forwardRef(
	(props: FormProps, ref: Ref<FormHandle>): ReactElement => {
		const {
			attachable,
			appItemsInfo,
			permissions,
			onAddAppAsTab,
			setRecentlyRemovedAppIds,
			setAppItemsInfo,
			onCreateSharedSpace,
			onEditSpace,
			appIdToScroll,
			onAppIdToScrollChange,
			onChangeIsBusy
		} = props;

		const [addNewItemMenu, setAddNewItemMenu] = useState<ActionsMenuState>({ isVisible: false });

		const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
			value: "",
			version: newGuid()
		});

		const [clientIdToAutoFocus, setClientIdToAutoFocus] = useState<Guid | null>(null);

		const addNewItemButtonRef: RefObject<HTMLButtonElement | null> = useRef(null);
		const [deleteConfirmationDialogElement, openDeleteConfirmationDialog] = useDeleteConfirmationDialog();

		const autoFocusState = useMemo(
			(): AutoFocusState<Guid> => {
				return {
					itemIdToFocus: clientIdToAutoFocus,
					changeTo: (clientId) => setClientIdToAutoFocus(clientId)
				};
			},
			[clientIdToAutoFocus]
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				const {
					value
				} = attachable;

				const itemsCopy: GlobalNavigationItemFormModel[] = [...value.items];
				const item: GlobalNavigationItemFormModel = itemsCopy.splice(fromIndex, 1)[0];
				itemsCopy.splice(toIndex, 0, item);

				attachable.for("items")
					.reducer
					.setValue(itemsCopy);
			},
			[attachable]
		);

		const removeAppHandler = useCallback(
			(installedAppId: number): void => {
				setRecentlyRemovedAppIds(items => [...items, installedAppId ]);
				setAppItemsInfo((existingApps) => {
					const result: Map<number, AppGlobalNavigationItemInfo> = new Map(existingApps);
					result.delete(installedAppId);
					return result;
				});
			},
			[setAppItemsInfo, setRecentlyRemovedAppIds]
		);

		const unGroupItemsHandler = useCallback(
			(index: number): void => {
				const groupAttachable: IValueInputAttachable<GroupGlobalNavigationItemFormModel> = attachable.for("items")
					.forIndex(index)
					.assertType(isGroupItem);

				attachable.for("items")
					.reducer
					.insertAt(
						index + 1,
						...groupAttachable.value
							.groupData
							.children
					);

				groupAttachable.for("groupData")
					.for("children")
					.reducer
					.setValue(EMPTY_ARRAY);
			},
			[attachable]
		);

		const deleteHandler = useCallback(
			(index: number): void => {
				attachable.for("items")
					.reducer
					.removeAt(index);
			},
			[attachable]
		);

		const addNewItemClickHandler = (args: ReorderableListAddButtonClickEventArgs): void => {
			const {
				clientX,
				clientY
			} = args;

			setAddNewItemMenu({
				isVisible: true,
				coordinate: { x: clientX, y: clientY }
			});
		};

		const itemAddedHandler = useCallback(
			(): void => {
				setSearchTerm({ value: "", version: newGuid() });
			},
			[]
		);

		const createSharedSpaceHandler = useCallback(
			async (): Promise<void> => {
				setAddNewItemMenu({ isVisible: false });

				const result: TryResult<SavedSpace> = await onCreateSharedSpace();

				if (!result.wasSuccessful) {
					return;
				}

				const space: SavedSpace = result.result;

				if (space.kind !== SpaceKind.Shared) {
					throw new Error(`Unexpected space kind was created: '${space.kind}'`);
				}

				const item: SpaceGlobalNavigationItemFormModel = setClientId({
					id: null,
					kind: GlobalNavigationItemKind.Space,
					spaceData: {
						spaceId: space.id
					},
					spaceKind: space.kind,
					name: space.name,
					userInfo: space.userInfo
				});

				setClientIdToAutoFocus(getClientId(item));

				attachable.for("items")
					.reducer
					.push(item);

				itemAddedHandler();
			},
			[
				attachable,
				itemAddedHandler,
				onCreateSharedSpace
			]
		);

		const createGroupHandler = useCallback(
			(): void => {
				setAddNewItemMenu({isVisible: false});

				const item: GlobalNavigationItemFormModel = setClientId(
					{
						id: null,
						kind: GlobalNavigationItemKind.Group,
						groupData: {
							name: "",
							children: []
						}
					}
				);

				setClientIdToAutoFocus(getClientId(item));

				attachable.for("items")
					.reducer
					.push(item);

				itemAddedHandler();
			},
			[
				attachable,
				itemAddedHandler
			]
		);

		const addAppHandler = (): void => {
			onAddAppAsTab(null);
			setAddNewItemMenu({isVisible: false});
		};

		const normalizedSearchTerm: string = useMemo(
			() => normalizeForSearch(searchTerm.value),
			[searchTerm]
		);

		const getName = useCallback(
			(item: GlobalNavigationItemFormModel): string => {
				switch (item.kind) {
					case GlobalNavigationItemKind.InstalledApp:
						return appItemsInfo.get(
							item.appData
								.installedAppId
						)?.name ?? "";
					case GlobalNavigationItemKind.Space:
						return item.name;
					case GlobalNavigationItemKind.Group:
						return item.groupData
							.name;
					default:
						never(item);
				}
			},
			[appItemsInfo]
		);

		const doesMatchSearchTerm = useCallback(
			(item: GlobalNavigationItemFormModel): boolean => {
				if (searchTerm.value === "") {
					return true;
				}
				const doesNameContainSearchTerm: boolean = contains(
					getName(item),
					normalizedSearchTerm
				);

				if (doesNameContainSearchTerm) {
					return true;
				}

				if (isGroupItem(item)) {
					const doesAnyChildMatchSearchTerm: boolean = item.groupData
						.children
						.some(doesMatchSearchTerm);

					if (doesAnyChildMatchSearchTerm) {
						return true;
					}
				}

				return false;
			},
			[
				getName,
				normalizedSearchTerm,
				searchTerm
			]
		);

		const getOverlayTextHandler = useCallback(
			(activeIndex: number): string => {
				const activeItem: GlobalNavigationItemFormModel = attachable.for("items")
					.forIndex(activeIndex)
					?.value;

				return isNotNil(activeItem)
					? getName(activeItem)
					: "";
			},
			[attachable, getName]
		);

		const groups = useMemo(
			(): readonly GlobalNavigationGroupAndIndex[] => {
				return attachable.for("items")
					.value
					.map((item, index): GlobalNavigationGroupAndIndex | null => {
						if (item.kind !== GlobalNavigationItemKind.Group) {
							return null;
						}

						return {
							name: item.groupData.name,
							index
						};
					})
					.filter(isNotNil);
			},
			[attachable]
		);

		const moveToGroupHandler = useCallback(
			(
				itemIndex: number,
				sourceGroupIndex: number | null,
				targetGroupIndex: number
			): void => {
				const sourceAttachable: IValueInputAttachable<readonly GlobalNavigationItemFormModel[]> = isNotNil(sourceGroupIndex)
					? attachable.for("items")
						.forIndex(sourceGroupIndex)
						.assertType(isGroupItem)
						.for("groupData")
						.for("children")
					: attachable.for("items");

				const targetAttachable: IValueInputAttachable<readonly GlobalNavigationItemFormModel[]> = attachable.for("items")
					.forIndex(targetGroupIndex)
					.assertType(isGroupItem)
					.for("groupData")
					.for("children");

				targetAttachable.reducer
					.push(sourceAttachable.value[itemIndex]);

				sourceAttachable.reducer
					.removeAt(itemIndex);
			},
			[attachable]
		);

		const moveOutOfGroupHandler = useCallback(
			(itemIndex: number, sourceGroupIndex: number): void => {
				const sourceAttachable: IValueInputAttachable<readonly GlobalNavigationItemFormModel[]> = attachable
					.for("items")
					.forIndex(sourceGroupIndex)
					.assertType(isGroupItem)
					.for("groupData")
					.for("children");

				const targetAttachable: IValueInputAttachable<readonly GlobalNavigationItemFormModel[]> = attachable
					.for("items");

				targetAttachable.reducer
					.insertAt(sourceGroupIndex + 1, sourceAttachable.value[itemIndex]);

				sourceAttachable.reducer
					.removeAt(itemIndex);
			},
			[attachable]
		);

		useImperativeHandle(
			ref,
			() => ({
				createGroup: createGroupHandler
			}),
			[createGroupHandler]
		);

		return (
			<>
				<SearchInputForm
					value={searchTerm}
					onChange={(value, version) => setSearchTerm({ value, version })}
					trackingId="search-term"
					placeholder={(
						<FormattedText
							text={localize("_FilterByProperties")}
							thingsToReplace={[
								{
									what: "{{Properties}}",
									filler: (
										<Typography weight="bold" isItalic>
											{localize("_SpacesAndGroups")}
										</Typography>
									)
								}
							]}
						/>
					)}
					hasMarginBottom
					height="large"
					icon="filter"
				/>
				<div className={styles.alertBar}>
					<AlertBar
						kind="info"
						isDismissable={false}
						isInline
					>
						{localize("_ManageNavigationSidebarInfo")}
					</AlertBar>
				</div>
				<ReorderableList
					onItemMove={!normalizedSearchTerm ? itemMoveHandler : undefined}
					hasSeparator
					getOverlayText={getOverlayTextHandler}
				>
					{attachable.for("items").map((itemToRender, index) => (
						itemToRender
							.when(
								doesMatchSearchTerm,
								(item) => (
									<GlobalNavigationListItem
										itemHandler={item}
										autoFocusState={autoFocusState}
										appItemsInfo={appItemsInfo}
										index={index}
										doesMatchSearchTerm={doesMatchSearchTerm}
										onDelete={() => deleteHandler(index)}
										onCreateSharedSpace={onCreateSharedSpace}
										onEditSpace={onEditSpace}
										permissions={permissions}
										onRemoveApp={removeAppHandler}
										onOpenDeleteConfirmation={openDeleteConfirmationDialog}
										onUnGroupChildren={unGroupItemsHandler}
										onAddAppAsTab={onAddAppAsTab}
										containingGroupIndex={null}
										groups={groups}
										onMoveToGroup={moveToGroupHandler}
										onMoveOutOfGroup={moveOutOfGroupHandler}
										key={getItemUniqueKey(item.value)}
										appIdToScroll={appIdToScroll}
										onAppIdToScrollChange={onAppIdToScrollChange}
										onItemAdded={itemAddedHandler}
										onChangeIsBusy={onChangeIsBusy}
									/>
								)
							)
					))}
				</ReorderableList>
				{permissions.canManageGlobalNavigation && (
					<ReorderableList.AddButton
						ref={addNewItemButtonRef}
						onClick={addNewItemClickHandler}
						trackingId="add-button"
					/>
				)}
				{(addNewItemMenu.isVisible && addNewItemButtonRef.current) && (
					<DropOverActionList
						target={addNewItemButtonRef.current}
						onClose={() => setAddNewItemMenu({ isVisible: false })}
						contentAlignment="tl"
						targetAlignment={{
							kind: "absolute",
							coordinate: addNewItemMenu.coordinate
						}}
						menuTitle={localize("_Add")}
					>
						<ActionListItem
							onClick={createSharedSpaceHandler}
							text={localize("_CreateSharedSpace")}
							trackingId="create-shared-space"
							icon="objects-column"
						/>
						<ActionListItem
							onClick={addAppHandler}
							text={localize("_AddWebContentAsSpace")}
							trackingId="open-app-as-tab"
							icon="code"
						/>
						<ActionListItem
							onClick={createGroupHandler}
							text={localize("_CreateGroup")}
							trackingId="create-group"
							icon="layer-group"
						/>
					</DropOverActionList>
				)}
				{deleteConfirmationDialogElement}
			</>
		);
	}
);
Form.displayName = "Form";
